import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Music, ExternalLink } from 'lucide-react';
import { formatSchedule } from '@/utils/formatters';
import PhotoGallery from '@/components/displays/PhotoGallery';

interface Display {
  id: string;
  name: string;
  description: string;
  image_url: string;
  photos: string[];
  location: string;
  schedule: string;
  display_type: string;
  holiday_type: string;
  review_rating: number;
  songCount: number;
  holidays: string[];
}

interface DisplayViewProps {
  display: Display;
}

const DisplayView: React.FC<DisplayViewProps> = ({ display }) => {
  const fallbackImage = 'https://placehold.co/200x200/1a1f2e/ffffff?text=No+Image';

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header Section with Image and Button */}
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Display Image */}
          <div className="flex-shrink-0">
            <img
              src={display.image_url || fallbackImage}
              alt={display.name}
              className="w-full sm:w-[200px] h-[200px] object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.src = fallbackImage;
              }}
            />
          </div>

          {/* Display Info and Button */}
          <div className="flex-1 flex flex-col">
            <h1 className="text-3xl font-bold mb-3">{display.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary">{display.display_type}</Badge>
              {display.holidays && display.holidays.map((holiday) => (
                <Badge key={holiday} variant="secondary">{holiday}</Badge>
              ))}
            </div>

            {/* View Display Button */}
            <div className="mt-auto">
              <Link to={`/display/${display.id}`}>
                <Button variant="default" size="lg" className="w-full sm:w-auto">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Full Display Page
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Display Details */}
        <div className="space-y-4">
          {display.description && (
            <p className="text-muted-foreground">{display.description}</p>
          )}

          <div className="flex items-center space-x-2 text-muted-foreground">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span>{display.location}</span>
          </div>

          <div className="flex items-center space-x-2 text-muted-foreground">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span>{formatSchedule(display.schedule)}</span>
          </div>

          <div className="flex items-center space-x-2 text-muted-foreground">
            <Music className="w-4 h-4 flex-shrink-0" />
            <span>{display.songCount} songs</span>
          </div>
        </div>

        {/* Photo Gallery */}
        {(display.photos && display.photos.length > 0) || display.image_url ? (
          <PhotoGallery
            photos={display.photos?.length > 0 ? display.photos : display.image_url ? [display.image_url] : []}
            displayName={display.name}
          />
        ) : null}
      </div>
    </Card>
  );
};

export default DisplayView; 